/**
 * Agents tab (`ldt-agent`, spec §16):
 *   - Aircraft Agent card (telemetry-style stats)
 *   - GCS Agent card
 *   - Cloud Processing (Flink) accordion with services list
 *   - YAML editor section with 3-mode modal (access/save/restart)
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { DroneLive } from '@/lib/store/drones';
import { aaClass, gcsClass } from '@/lib/store/drones';
import type { BadgeClass } from '@/lib/store/drones';
import { useRtmStore } from '@/lib/store/rtm';
import { useInventoryDrones } from '@/lib/api/queries';
import { http } from '@/lib/api/http';
import { Section, SectionGroup } from '@/components/Section';
import { Dot } from '@/components/ui/dot';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Accordion } from '@/components/ui/accordion';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { formatAgo } from '@/lib/format';
import { Lock } from 'lucide-react';
import { useCustodianDrone, useCustodianHealth } from '@/lib/api/queries';
import { aircraftAgentFromCustodian, gcsAgentFromCustodian } from '@/lib/custodianAgents';
import { droneContextFlags } from '@/lib/droneContextFlags';

export function AgentsTab({ drone }: { drone: DroneLive }) {
  const c2 = drone.c2;
  const cloud = useRtmStore((s) => s.cloud);
  const aaTone = aaClass(c2?.aa_agent_status ?? null, c2?.aa_latency_ms ?? null);
  const gcsTone = gcsClass(c2?.gcs_agent_status ?? null, c2?.gcs_latency_ms ?? null);
  const cloudTone: BadgeClass = cloud?.status === 'Running' ? 'ok' : cloud?.status === 'Degraded' ? 'warn' : cloud?.status === 'Stopped' ? 'crit' : 'muted';
  const onlineJobs = cloud?.jobs.filter((j) => j.status === 'Online').length ?? 0;
  // ENG-271: the live feed publishes no agent stream, so link what the Custodian
  // API does know (adapter connected, GCS plugin). A real c2 envelope still wins.
  const { data: cDrone } = useCustodianDrone(drone.id);
  const { data: cHealth } = useCustodianHealth(drone.id);
  const cAircraft = aircraftAgentFromCustodian(cDrone, cHealth, drone.telemetry.pipeline_delay_s);
  const cGcs = gcsAgentFromCustodian(cDrone, cHealth);
  const aaFromFeed = c2?.aa_agent_status != null;
  const gcsFromFeed = c2?.gcs_agent_status != null;

  return (
    <SectionGroup>
      <Section title="Aircraft Agent">
        <AgentCard
          name={c2?.aa_agent_id ? `Aircraft Agent — ${c2.aa_agent_id}` : (cAircraft?.name ?? 'Aircraft Agent')}
          sub={c2?.aa_platform ?? cAircraft?.sub ?? '—'}
          tone={aaFromFeed || !cAircraft ? aaTone : cAircraft.tone}
          status={c2?.aa_agent_status ?? cAircraft?.status ?? '—'}
          latencyMs={c2?.aa_latency_ms ?? cAircraft?.latencyMs}
          msgRate={c2?.aa_msg_rate}
          lastMsgAt={c2?.aa_last_msg_at ?? cAircraft?.lastMsgAt}
          producer={
            c2?.aa_producer_id
              ? {
                  tenant: c2.aa_tenant ?? null,
                  role: 'aircraft',
                  droneId: drone.id,
                  producerId: c2.aa_producer_id ?? null,
                  lastMsgAt: c2.aa_last_msg_at ?? null,
                }
              : undefined
          }
        />
      </Section>

      <Section title="GCS Agent">
        <AgentCard
          name={c2?.gcs_platform_name ? `GCS Agent — ${c2.gcs_platform_name}` : (cGcs?.name ?? 'GCS Agent')}
          sub={c2?.gcs_platform ?? cGcs?.sub ?? '—'}
          tone={gcsFromFeed || !cGcs ? gcsTone : cGcs.tone}
          status={c2?.gcs_agent_status ?? cGcs?.status ?? '—'}
          latencyMs={c2?.gcs_latency_ms ?? cGcs?.latencyMs}
          msgRate={c2?.gcs_msg_rate}
          lastMsgAt={c2?.gcs_last_msg_at ?? cGcs?.lastMsgAt}
          producer={
            c2?.gcs_producer_id
              ? {
                  tenant: c2.gcs_tenant ?? null,
                  role: 'gcs',
                  droneId: drone.id,
                  producerId: c2.gcs_producer_id ?? null,
                  lastMsgAt: c2.gcs_last_msg_at ?? null,
                }
              : undefined
          }
        />
      </Section>

      {droneContextFlags.agents.cloudProcessing && (
        <Section title="Cloud Processing">
          <article className="rounded-md border border-(--color-border) bg-(--color-surface) p-3">
            <header className="flex items-center justify-between gap-3 mb-2">
              <div>
                <div className="text-[0.846em] font-bold">{cloud?.pipeline_name ?? '—'}</div>
                <div className="text-[0.692em] font-mono text-(--color-text3)">{cloud?.pipeline_sub ?? '—'}</div>
              </div>
              <div className="flex items-center gap-1.5">
                <Dot variant={cloudTone === 'muted' ? 'muted' : cloudTone} size="sm" />
                <span
                  className={cn(
                    'text-[0.769em] font-bold',
                    cloudTone === 'ok' && 'text-(--color-green)',
                    cloudTone === 'warn' && 'text-(--color-amber)',
                    cloudTone === 'crit' && 'text-(--color-red)',
                    cloudTone === 'muted' && 'text-(--color-text3)',
                  )}
                >
                  {cloud?.status ?? '—'}
                </span>
              </div>
            </header>
            {cloud && cloud.jobs.length > 0 ? (
              <Accordion
                title="Pipeline services"
                meta={
                  <span className={onlineJobs === cloud.jobs.length ? 'text-(--color-green) font-bold' : 'text-(--color-amber) font-bold'}>
                    {onlineJobs}/{cloud.jobs.length} online
                  </span>
                }
              >
                <ul className="space-y-0.5">
                  {cloud.jobs.map((j) => {
                    const tone: BadgeClass = j.status === 'Online' ? 'ok' : j.status === 'Degraded' ? 'warn' : 'crit';
                    return (
                      <li key={j.name} className="flex items-center gap-2 text-[0.692em] font-mono py-1">
                        <Dot variant={tone} size="xs" />
                        <span className="flex-1 truncate">{j.name}</span>
                        <Badge variant={tone}>{j.status}</Badge>
                      </li>
                    );
                  })}
                </ul>
              </Accordion>
            ) : (
              <div className="text-[0.769em] text-(--color-text3) text-center py-3">No pipeline data.</div>
            )}
          </article>
        </Section>
      )}

      {droneContextFlags.agents.yamlEditor && <YamlEditorSection droneId={drone.id} />}
    </SectionGroup>
  );
}

interface ProducerInfo {
  tenant: string | null;
  role: 'aircraft' | 'gcs';
  droneId: string;
  producerId: string | null;
  /** ISO timestamp of the last message from this producer — drives the
   *  chip's activity dot. */
  lastMsgAt: string | null;
}

function AgentCard({
  name,
  sub,
  tone,
  status,
  latencyMs,
  msgRate,
  lastMsgAt,
  producer,
}: {
  name: string;
  sub: string;
  tone: BadgeClass;
  status: string;
  latencyMs?: number | null;
  msgRate?: number | null;
  lastMsgAt?: string | null;
  producer?: ProducerInfo;
}) {
  // Split formatAgo's "Xs ago" / "Xm ago" output into magnitude + unit so the
  // cell can match the latency-tile typographic style (big number, small unit).
  let lastMsgValue = '—';
  let lastMsgUnit = '';
  if (lastMsgAt) {
    const ago = formatAgo(lastMsgAt); // e.g. "2s ago" / "1m ago"
    const m = ago.match(/^(\d+)([smhd])/);
    if (m) {
      lastMsgValue = m[1]!;
      lastMsgUnit = ` ${m[2]} ago`;
    } else {
      lastMsgValue = ago;
    }
  }

  return (
    <article className="rounded-md border border-(--color-border) bg-(--color-surface) p-3">
      <header className="flex items-center justify-between gap-3 mb-2">
        <div>
          <div className="text-[0.846em] font-bold">{name}</div>
          <div className="text-[0.692em] font-mono text-(--color-text3)">{sub}</div>
        </div>
        <div className="flex items-center gap-1.5">
          <Dot variant={tone === 'muted' ? 'muted' : tone} size="sm" />
          <span
            className={cn(
              'text-[0.769em] font-bold',
              tone === 'ok' && 'text-(--color-green)',
              tone === 'warn' && 'text-(--color-amber)',
              tone === 'crit' && 'text-(--color-red)',
              tone === 'muted' && 'text-(--color-text3)',
            )}
          >
            {status}
          </span>
        </div>
      </header>
      {producer && <ProducerChip info={producer} />}
      {/* ENG-271: only the figures the source really has; no blank cells. */}
      {(latencyMs != null || msgRate != null || lastMsgAt) && (
        <div
          className="grid gap-2 text-center"
          style={{
            gridTemplateColumns: `repeat(${[latencyMs != null, !!lastMsgAt, msgRate != null].filter(Boolean).length}, minmax(0, 1fr))`,
          }}
        >
          {latencyMs != null && (
            <div>
              <div className={cn('text-[0.846em] font-bold font-mono', tone === 'ok' && 'text-(--color-green)', tone === 'warn' && 'text-(--color-amber)', tone === 'crit' && 'text-(--color-red)')}>
                {latencyMs}
                <span className="text-[0.615em] font-normal"> ms</span>
              </div>
              <div className="text-[0.615em] text-(--color-text3) uppercase tracking-wider mt-0.5">Latency</div>
            </div>
          )}
          {lastMsgAt && (
            <div>
              <div className="text-[0.846em] font-bold font-mono">
                {lastMsgValue}
                {lastMsgUnit && <span className="text-[0.615em] font-normal">{lastMsgUnit}</span>}
              </div>
              <div className="text-[0.615em] text-(--color-text3) uppercase tracking-wider mt-0.5">Last Msg</div>
            </div>
          )}
          {msgRate != null && (
            <div>
              <div className="text-[0.846em] font-bold font-mono">
                {msgRate.toFixed(1)}
                <span className="text-[0.615em] font-normal"> /s</span>
              </div>
              <div className="text-[0.615em] text-(--color-text3) uppercase tracking-wider mt-0.5">Msg Rate</div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

/* --------------- Producer attribution chip (ticket 86e1hnwf5 item 1) ---------------
 *
 * Surfaces the MQTT-topic data-chain attribution under each agent card:
 *   ● airmarket › aircraft › SIMTL70
 *     producer:aircraft/001                       updated 0.4 s ago
 *
 * The activity dot here is INDEPENDENT of the agent's self-reported
 * status badge in the card header — the badge says "the agent claims
 * Online", the chip dot says "we are actually receiving its messages
 * right now". They can disagree (stale agent that hasn't dropped its
 * status), which is exactly the ambiguity the ticket calls out.
 */

function ProducerChip({ info }: { info: ProducerInfo }) {
  // 1 s ticker so the "updated Xs ago" text decays AND the dot fades
  // when the producer goes silent — even without new MQTT messages
  // triggering a re-render via store updates.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 1_000);
    return () => window.clearInterval(id);
  }, []);

  const ageMs = info.lastMsgAt ? Date.now() - Date.parse(info.lastMsgAt) : Number.POSITIVE_INFINITY;
  const activity: BadgeClass =
    !info.producerId ? 'muted' :
    ageMs <= 3_000 ? 'ok' :
    ageMs <= 10_000 ? 'warn' :
    'crit';
  const updated = info.lastMsgAt ? formatAgo(info.lastMsgAt) : '—';

  // Render the breadcrumb. Tenant + role + drone segments are shown as
  // muted text; producer is bold for at-a-glance scanning.
  const segments = [
    info.tenant ?? '—',
    info.role,
    info.droneId,
  ];

  return (
    <div className="rounded-md border border-(--color-border-lite) bg-(--color-bg) px-2.5 py-1.5 mb-2">
      <div className="flex items-center gap-1.5 text-[0.692em] font-mono text-(--color-text3) min-w-0">
        <Dot variant={activity === 'muted' ? 'muted' : activity} size="xs" />
        <span className="truncate">
          {segments.map((s, i) => (
            <span key={i}>
              {i > 0 && <span className="text-(--color-border) mx-1">›</span>}
              {s}
            </span>
          ))}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2 mt-0.5 min-w-0">
        <span className={cn(
          'text-[0.769em] font-mono font-bold truncate',
          activity === 'ok' && 'text-(--color-text)',
          activity === 'warn' && 'text-(--color-amber)',
          activity === 'crit' && 'text-(--color-red)',
          activity === 'muted' && 'text-(--color-text3) italic',
        )}>
          {info.producerId
            ? `producer:${info.producerId}`
            : (info.role === 'gcs'
                ? 'No GCS producer connected'
                : 'No producer connected')}
        </span>
        <span className="text-[0.615em] font-mono text-(--color-text3) shrink-0">
          {info.producerId ? `updated ${updated}` : ''}
        </span>
      </div>
    </div>
  );
}

/* --------------- YAML editor with 3-mode modal flow --------------- */

type YamlState = 'locked' | 'clean' | 'dirty' | 'saving' | 'restarting';

function YamlEditorSection({ droneId }: { droneId: string }) {
  const qc = useQueryClient();
  const { data, isPending, isError } = useInventoryDrones();
  const inv = data?.find((d) => d.drone_id === droneId);

  const [state, setState] = useState<YamlState>('locked');
  const [yamlText, setYamlText] = useState('');
  const [modal, setModal] = useState<null | 'access' | 'save' | 'restart'>(null);

  useEffect(() => {
    if (inv && state === 'locked' && inv.yaml_created) {
      setYamlText(inv.yaml);
    }
  }, [inv, state]);

  const save = useMutation({
    mutationFn: async (text: string) => http.post(`api/drones/${droneId}/yaml`, { json: { yaml: text } }).json<{ saved: boolean }>(),
    onSuccess: () => {
      setState('clean');
      setModal(null);
      toast.success('YAML saved');
      qc.invalidateQueries({ queryKey: ['inventory', 'drones'] });
    },
    onError: () => toast.error('Save failed'),
  });

  const restart = useMutation({
    mutationFn: () => http.post(`api/drones/${droneId}/agent/restart`).json<{ restarted: boolean }>(),
    onMutate: () => setState('restarting'),
    onSuccess: () => {
      setState('clean');
      setModal(null);
      toast.success('Agent restarted');
    },
    onError: () => {
      setState('clean');
      toast.error('Restart failed');
    },
  });

  if (isPending) {
    return (
      <Section title="YAML Configuration">
        <Skeleton className="h-40 w-full" />
      </Section>
    );
  }
  if (isError || !inv) {
    return (
      <Section title="YAML Configuration">
        <div className="text-[0.769em] text-(--color-red)">Failed to load drone config.</div>
      </Section>
    );
  }

  const lines = yamlText.split('\n').length;

  return (
    <Section title="YAML Configuration">
      <div className="rounded-md border border-(--color-border) bg-(--color-surface) overflow-hidden">
        <header className="flex items-center justify-between px-3 py-1.5 border-b border-(--color-border-lite) bg-(--color-bg)">
          <span className="text-[0.692em] font-mono text-(--color-text2) truncate">{droneId}.yaml</span>
          <div className="flex items-center gap-1.5">
            {state === 'dirty' && <Badge variant="warn">Unsaved</Badge>}
            {state === 'clean' && inv.yaml_created && <Badge variant="ok">Saved</Badge>}
            {state === 'locked' && <Badge variant="muted"><Lock size={10} /> Locked</Badge>}
            <Button size="xs" variant="outline" disabled={state !== 'dirty'} onClick={() => setModal('save')}>Save</Button>
            <Button size="xs" variant="ghost" disabled={state !== 'dirty'} onClick={() => { setYamlText(inv.yaml); setState('clean'); }}>Discard</Button>
            <Button size="xs" variant="danger" disabled={state !== 'clean' || !inv.yaml_created} onClick={() => setModal('restart')}>Restart Agent</Button>
          </div>
        </header>
        {state === 'locked' || !inv.yaml_created ? (
          <div className="p-6 text-center space-y-2">
            <Lock size={32} className="mx-auto text-(--color-text3)" />
            <div className="text-[0.846em] font-semibold">Edit access required</div>
            <div className="text-[0.769em] text-(--color-text3)">Editing the aircraft agent YAML configuration requires operator-level permissions.</div>
            <Button onClick={() => setModal('access')}>Request Edit Access</Button>
          </div>
        ) : (
          <>
            <textarea
              value={yamlText}
              onChange={(e) => {
                setYamlText(e.target.value);
                setState(e.target.value === inv.yaml ? 'clean' : 'dirty');
              }}
              spellCheck={false}
              className="w-full min-h-[280px] p-3 font-mono text-[0.769em] bg-(--color-bg) outline-none resize-y"
            />
            <footer className="flex items-center justify-between px-3 py-1 text-[0.615em] font-mono text-(--color-text3) border-t border-(--color-border-lite)">
              <span className="flex items-center gap-1.5">
                <Dot variant={state === 'dirty' ? 'warn' : 'ok'} size="xs" />
                {state === 'dirty' ? 'Unsaved changes — click Save' : state === 'restarting' ? 'Agent restarting…' : 'No pending changes'}
              </span>
              <span>{lines} lines</span>
            </footer>
          </>
        )}
      </div>

      {/* Access modal */}
      <Dialog open={modal === 'access'} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Edit Access</DialogTitle>
            <DialogDescription>Editing the aircraft agent YAML configuration requires operator-level permissions. Your account has been verified.</DialogDescription>
          </DialogHeader>
          <div className="text-[0.769em] text-(--color-green) flex items-center gap-1.5">
            ✓ Edit access confirmed — Demo User
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={() => { setState('clean'); setModal(null); }}>Open for Editing</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save modal */}
      <Dialog open={modal === 'save'} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Configuration?</DialogTitle>
            <DialogDescription>
              Changes will be persisted. The agent must be restarted for the new YAML to take effect. Restarts cause a brief telemetry gap.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setModal(null)} disabled={save.isPending}>Cancel</Button>
            <Button onClick={() => save.mutate(yamlText)} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restart modal */}
      <Dialog open={modal === 'restart'} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restart Aircraft Agent?</DialogTitle>
            <DialogDescription>
              The agent will briefly disconnect during restart. Telemetry will resume within a few seconds.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setModal(null)} disabled={restart.isPending}>Cancel</Button>
            <Button variant="danger" onClick={() => restart.mutate()} disabled={restart.isPending}>
              {restart.isPending ? 'Restarting…' : 'Restart Agent'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}
